import { useEffect, useMemo, useState } from 'react';
import { useConfigService } from '@thd-nucleus/experience-context';
import { DEFAULT_GIFT_CARD_BARCODE_TIMEOUT, FEATURE_SWITCH_NAMES } from '../utils/consts';
import { getCookie } from '../utils/utils';

export const useBarcodeTimer = () => {
  const featureSwitchConfigValue = useConfigService(
    FEATURE_SWITCH_NAMES.GIFT_CARD_BARCODE_TIMEOUT
  ) as unknown;
  const cookieValue = getCookie(
    FEATURE_SWITCH_NAMES.GIFT_CARD_BARCODE_TIMEOUT as string
  );

  const timeoutMinutes = useMemo(() => {
    const raw = featureSwitchConfigValue ?? cookieValue ?? DEFAULT_GIFT_CARD_BARCODE_TIMEOUT;
    const asNumber = Number(raw);
    if (!Number.isFinite(asNumber) || asNumber <= 0) return DEFAULT_GIFT_CARD_BARCODE_TIMEOUT;
    return Math.floor(asNumber);
  }, [featureSwitchConfigValue, cookieValue]);

  // Compute a fixed expiry timestamp at mount. Using a constant expiryTimeMs
  // ensures re-renders won't change the intended expiry — the timer counts
  // down from the moment the component mounted.
  const expiryTimeMs = useMemo(
    () => Date.now() + timeoutMinutes * 60 * 1000,
    [timeoutMinutes]
  );

  const computeRemainingSeconds = () => Math.max(0, Math.ceil((expiryTimeMs - Date.now()) / 1000));
  const computeRemainingMinutes = () => Math.max(0, Math.ceil((expiryTimeMs - Date.now()) / (60 * 1000)));

  // Keep the displayed remaining minutes in state. Initialize from the
  // computed value so the first render shows the correct value.
  const [remainingMinutes, setRemainingMinutes] = useState<number>(
    computeRemainingMinutes()
  );

  useEffect(() => {
    // `sync` will recompute minutes and only set state if the displayed value
    // actually changes. This prevents unnecessary re-renders while still
    // keeping the minute label correct.
    const sync = () => {
      const mins = computeRemainingMinutes();
      setRemainingMinutes((prev) => (prev === mins ? prev : mins));
    };

    // Run an immediate sync so the UI is correct on mount.
    sync();

    // Use a short interval (1s) so `expired` remains accurate between minute
    // ticks — we don't update state every second thanks to the `sync`
    // no-op when minutes haven't changed. This is a pragmatic balance:
    // - keeps re-renders to ~1 per minute
    // - keeps `expired` accurate if someone reads it between minute updates
    const id = setInterval(() => {
      sync();
    }, 1000);

    // Cleanup to avoid leaks on unmount.
    return () => clearInterval(id);
  }, [expiryTimeMs]);

  const formattedMinutes = `${remainingMinutes} minute${
    remainingMinutes !== 1 ? 's' : ''
  }`;

  // `expired` is computed on-demand from seconds so it stays up-to-date even
  // between ticks (no need to store it in state).
  return {
    timeoutMinutes,
    remainingMinutes,
    formattedMinutes,
    expired: computeRemainingSeconds() <= 0
  } as const;
};

export default useBarcodeTimer;
